// Port of Offline/CoverageArea.swift (TrailBivvyKit): the area where a tile source or map provider has data — an
// optional bounding box refined by optional polygons (e.g. simplified country outlines). No box and no polygon means
// worldwide.
//
// Geometry is tested in the longitude/latitude plane (even–odd rule, holes honoured); antimeridian crossing is not
// supported. Invalid polygons (< 3 vertices) are ignored, and a polygon list without any valid polygon behaves like
// no polygon list. Rings are prepared once at construction, so the per-tile tests of the planner do not allocate.

import type { GeoBounds, GeoCoordinate, GeoPolygon } from '../core/models';
import { boundsContains, boundsExpanded, boundsIntersect, boundsOf, boundsUnion, isValidCoordinate, openRing, polygonDistance } from '../core/geo';
import { TileMath, tileIsValid, type TileCoordinate } from './TileMath';

/**
 * Tiles within this distance of a layer's coverage still count as covered, which absorbs the simplification of the
 * bundled coverage outlines (land borders ~0.4 km, coasts up to 1.5 km) so border and coastal tiles are never lost.
 * Border tiles may therefore be planned for two neighbouring layers. (Swift `TileLayerDescriptor.coverageMarginMeters`.)
 */
export const coverageMarginMeters = 1_500;

/** How a rectangle relates to the area. */
export type CoverageRelation = 'outside' | 'partial' | 'inside';

/** Swift `GeoPolygon.isValid`: at least three exterior vertices. */
export function polygonIsValid(polygon: GeoPolygon): boolean {
  return polygon.exterior.length >= 3;
}

/** A polygon prepared for fast, allocation-free tests (open rings as flat coordinate arrays with bounds). */
class PreparedPolygon {
  readonly bounds: GeoBounds;
  /** Exterior first, then holes; each ring as [xs, ys]. */
  readonly rings: Array<{ xs: Float64Array; ys: Float64Array }>;

  private constructor(bounds: GeoBounds, rings: Array<{ xs: Float64Array; ys: Float64Array }>) {
    this.bounds = bounds;
    this.rings = rings;
  }

  static make(polygon: GeoPolygon): PreparedPolygon | undefined {
    if (!polygonIsValid(polygon)) return undefined;
    const bounds = boundsOf(polygon.exterior);
    if (!bounds) return undefined;
    const exterior = openRing(polygon.exterior);
    if (exterior.length < 3) return undefined;
    const holes = polygon.holes.map(openRing).filter((h) => h.length >= 3);
    const rings = [exterior, ...holes].map((ring) => ({
      xs: Float64Array.from(ring, (c) => c.longitude),
      ys: Float64Array.from(ring, (c) => c.latitude),
    }));
    return new PreparedPolygon(bounds, rings);
  }

  /** Even–odd containment over all rings (a point in a hole is outside). */
  contains(x: number, y: number): boolean {
    const b = this.bounds;
    if (y < b.minLatitude || y > b.maxLatitude || x < b.minLongitude || x > b.maxLongitude) return false;
    let inside = false;
    for (const ring of this.rings) {
      const count = ring.xs.length;
      let j = count - 1;
      for (let i = 0; i < count; i++) {
        const yi = ring.ys[i];
        const yj = ring.ys[j];
        if (yi > y !== yj > y) {
          const xi = ring.xs[i];
          const xj = ring.xs[j];
          if (x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
        }
        j = i;
      }
    }
    return inside;
  }

  /**
   * Outside (no overlap), partial (a ring edge passes through the rectangle) or inside (no edge in the rectangle and
   * its centre inside the polygon).
   */
  relation(rect: GeoBounds): CoverageRelation {
    if (!boundsIntersect(this.bounds, rect)) return 'outside';
    const minX = rect.minLongitude;
    const maxX = rect.maxLongitude;
    const minY = rect.minLatitude;
    const maxY = rect.maxLatitude;
    for (const ring of this.rings) {
      const count = ring.xs.length;
      let j = count - 1;
      for (let i = 0; i < count; i++) {
        const ax = ring.xs[j];
        const ay = ring.ys[j];
        const bx = ring.xs[i];
        const by = ring.ys[i];
        j = i;
        // Quick rejects: both end points beyond the same side of the rectangle.
        if ((ax < minX && bx < minX) || (ax > maxX && bx > maxX) || (ay < minY && by < minY) || (ay > maxY && by > maxY)) {
          continue;
        }
        if (segmentIntersects(ax, ay, bx, by, minX, minY, maxX, maxY)) return 'partial';
      }
    }
    return this.contains((minX + maxX) / 2, (minY + maxY) / 2) ? 'inside' : 'outside';
  }
}

/** Liang–Barsky clip of segment a→b against the rectangle. */
function segmentIntersects(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  minX: number,
  minY: number,
  maxX: number,
  maxY: number,
): boolean {
  const dx = bx - ax;
  const dy = by - ay;
  let t0 = 0;
  let t1 = 1;
  const clip = (p: number, q: number): boolean => {
    if (p === 0) return q >= 0;
    const r = q / p;
    if (p < 0) {
      if (r > t1) return false;
      if (r > t0) t0 = r;
    } else {
      if (r < t0) return false;
      if (r < t1) t1 = r;
    }
    return true;
  };
  return clip(-dx, ax - minX) && clip(dx, maxX - ax) && clip(-dy, ay - minY) && clip(dy, maxY - ay);
}

/** The area where a tile source or map provider has data (see the module comment). */
export class CoverageArea {
  /** Bounding box limit; undefined = no box limit. */
  readonly bounds: GeoBounds | undefined;
  /** Valid polygons (undefined when there is no polygon limit). */
  readonly polygons: GeoPolygon[] | undefined;
  private readonly prepared: PreparedPolygon[];

  /**
   * @param bounds Box limit; undefined = none.
   * @param polygons Polygon limit; undefined (or no valid polygon) = none.
   */
  constructor(bounds?: GeoBounds, polygons?: readonly GeoPolygon[]) {
    this.bounds = bounds;
    const validPolygons: GeoPolygon[] = [];
    const prepared: PreparedPolygon[] = [];
    for (const polygon of polygons ?? []) {
      const p = PreparedPolygon.make(polygon);
      if (p) {
        validPolygons.push(polygon);
        prepared.push(p);
      }
    }
    this.polygons = validPolygons.length === 0 ? undefined : validPolygons;
    this.prepared = prepared;
  }

  /** No limit at all. */
  static readonly worldwide = new CoverageArea();

  /** `true` when neither a box nor a polygon limits the area. */
  get isWorldwide(): boolean {
    return this.bounds === undefined && this.polygons === undefined;
  }

  /** Smallest known box around the area (box ∩ polygon bounds), undefined when worldwide. */
  get boundingBox(): GeoBounds | undefined {
    let polygonBox: GeoBounds | undefined;
    for (const p of this.prepared) polygonBox = polygonBox ? boundsUnion(polygonBox, p.bounds) : p.bounds;
    const box = this.bounds;
    if (box && polygonBox) {
      return {
        minLatitude: Math.max(box.minLatitude, polygonBox.minLatitude),
        minLongitude: Math.max(box.minLongitude, polygonBox.minLongitude),
        maxLatitude: Math.min(box.maxLatitude, polygonBox.maxLatitude),
        maxLongitude: Math.min(box.maxLongitude, polygonBox.maxLongitude),
      };
    }
    return box ?? polygonBox;
  }

  /** Whether `coordinate` lies inside the area (exact test against the stored geometry). */
  contains(coordinate: GeoCoordinate): boolean {
    if (!isValidCoordinate(coordinate)) return false;
    if (this.bounds && !boundsContains(this.bounds, coordinate)) return false;
    if (this.polygons === undefined) return true;
    return this.prepared.some((p) => p.contains(coordinate.longitude, coordinate.latitude));
  }

  /**
   * Whether `coordinate` lies inside the area or within `meters` of its polygons/box (used near simplified coastlines
   * and borders). Swift `contains(_:withinMeters:)`.
   */
  containsWithin(coordinate: GeoCoordinate, meters: number): boolean {
    if (!isValidCoordinate(coordinate)) return false;
    if (this.contains(coordinate)) return true;
    const margin = Number.isFinite(meters) ? Math.max(0, meters) : 0;
    if (!(margin > 0)) return false;
    if (this.bounds && !boundsContains(boundsExpanded(this.bounds, margin), coordinate)) return false;
    const polygons = this.polygons;
    if (!polygons) return true;
    for (let i = 0; i < polygons.length; i++) {
      if (!boundsContains(boundsExpanded(this.prepared[i].bounds, margin), coordinate)) continue;
      if (polygonDistance(polygons[i], coordinate) <= margin) return true;
    }
    return false;
  }

  /** Whether the rectangle `other` overlaps the area (touching counts as overlapping). */
  intersects(other: GeoBounds): boolean {
    return this.relation(other) !== 'outside';
  }

  /**
   * Whether the tile is inside, or within `marginMeters` of, the area. Always `true` when worldwide; `false` for
   * invalid tile addresses.
   */
  coversTile(tile: TileCoordinate, marginMeters = coverageMarginMeters): boolean {
    if (!tileIsValid(tile)) return false;
    if (this.isWorldwide) return true;
    return this.relation(CoverageArea.searchBox(tile, marginMeters)) !== 'outside';
  }

  /** Same geometry (Swift `==`: box and polygons). */
  equals(other: CoverageArea): boolean {
    if (this === other) return true;
    if (JSON.stringify(this.bounds ?? null) !== JSON.stringify(other.bounds ?? null)) return false;
    const a = this.polygons;
    const b = other.polygons;
    if (a === undefined || b === undefined) return a === b;
    if (a.length !== b.length) return false;
    // Bundled outlines are shared objects: compare by reference first, by value only when needed.
    return a.every((p, i) => p === b[i] || JSON.stringify(p) === JSON.stringify(b[i]));
  }

  // MARK: - Relations (used by the planner's hierarchical tile filter and the map's coverage-clipped layers)

  /** The tile's bounds expanded by `marginMeters` (the box tested by `coversTile`). */
  static searchBox(tile: TileCoordinate, marginMeters: number): GeoBounds {
    const margin = Number.isFinite(marginMeters) ? Math.max(0, marginMeters) : 0;
    return boundsExpanded(TileMath.bounds(tile), margin);
  }

  /** Relation of the rectangle `rect` to the area. */
  relation(rect: GeoBounds): CoverageRelation {
    const values = [rect.minLatitude, rect.minLongitude, rect.maxLatitude, rect.maxLongitude];
    if (!values.every(Number.isFinite) || rect.minLatitude > rect.maxLatitude || rect.minLongitude > rect.maxLongitude) {
      return 'outside';
    }
    let boxRelation: CoverageRelation = 'inside';
    const bounds = this.bounds;
    if (bounds) {
      if (!boundsIntersect(bounds, rect)) return 'outside';
      const within =
        rect.minLatitude >= bounds.minLatitude &&
        rect.maxLatitude <= bounds.maxLatitude &&
        rect.minLongitude >= bounds.minLongitude &&
        rect.maxLongitude <= bounds.maxLongitude;
      if (!within) boxRelation = 'partial';
    }
    if (this.polygons === undefined) return boxRelation;
    let result: CoverageRelation = 'outside';
    for (const polygon of this.prepared) {
      const relation = polygon.relation(rect);
      if (relation === 'inside') return boxRelation;
      if (relation === 'partial') result = 'partial';
    }
    return result;
  }
}
